import { useEffect, useState } from "react";
import { WorldRenderer } from "../../WorldRenderer";
import { Scenario } from "../../../models/Scenario";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import { ExpandMore, PushPin, PushPinOutlined } from "@mui/icons-material";
import { WREvents } from "../../WREvents";
import { saveModel } from "../../../api/connection";
import { BaseAgent } from "../agents/BaseAgent";
import { AgentBlocks } from "../scenarios/AgentBlocks";
import { CameraAgent, ScenarioClient } from "../scenarios/ScenarioClient";
import { RenderAccordionToolbar } from "../../../components/Minibar";

export function ScenarioEditor({
  wr,
  size,
  position,
}: {
  wr: WorldRenderer;
  size: { width: number; height: number };
  position: { x: number; y: number };
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [client, setClient] = useState<ScenarioClient>();
  const [scenario, setScenario] = useState<Scenario>();
  const [editedScenario, setEditedScenario] = useState<Scenario>();
  const [agents, setAgents] = useState<BaseAgent[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const theme = useTheme();

  const toggleExpand = (id: string) => {
    if (expanded === id) {
      setExpanded(false);
    } else {
      setExpanded(id);
    }
  };

  const togglePin = (id: string) => {
    setPinned((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isOpen = (id: string) => expanded === id || pinned.has(id);

  useEffect(() => {
    if (!wr) return;

    const selectedScenarioListener = (scenario: Scenario) => {
      if (!scenario) {
        setScenario(undefined);
        setEditedScenario(undefined);
        setIsEditing(false);
        return;
      }
      setScenario(scenario);
      setEditedScenario(scenario);
      setIsEditing(true);
    };
    wr.onEvent(WREvents.ScenarioSelected, selectedScenarioListener);

    const agentsUpdatedListener = (agents: BaseAgent[]) => {
      setAgents([...agents, new CameraAgent()]);
    };
    wr.onEvent(WREvents.AgentsUpdated, agentsUpdatedListener);

    return () => {
      wr.off(WREvents.ScenarioSelected, selectedScenarioListener);
      wr.off(WREvents.AgentsUpdated, agentsUpdatedListener);
    };
  }, [wr]);

  useEffect(() => {
    if (!client) return;
    client.on("status", (status) => {
      console.log(`ScenarioClient status: ${status}`);
      if (status === "completed") {
        setClient(undefined);
      }
    });
  }, [client]);

  if (!editedScenario) {
    return null;
  }

  const mixer = (change: Partial<Scenario>) => {
    setEditedScenario({ ...editedScenario, ...change });
  };

  const haveBlocksForAgent = (agent: BaseAgent) => {
    return (
      editedScenario.blocks.filter((b) => b.actorId === agent.visual._id)
        .length > 0
    );
  };

  const commitChanges = async () => {
    Object.assign(scenario!, editedScenario);
    await saveModel(scenario!);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        pointerEvents: isEditing ? "all" : "none",
        backgroundColor: isEditing
          ? "rgba(200, 200, 200, 0.15)"
          : "transparent",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: 1.25,
          display: "flex",
          gap: theme.spacing(2),
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ flexGrow: 1, paddingLeft: theme.spacing(1) }}>
          <Typography variant="h6" component="span" sx={{ marginRight: 1 }}>
            Scenario Name:
          </Typography>
          <TextField
            variant="standard"
            value={editedScenario.name}
            onChange={(e) => mixer({ name: e.target.value })}
          />
        </Box>
        <Box sx={{ flexGrow: 0 }}>
          <Button
            disabled={client === undefined}
            color="error"
            onClick={() => client?.stop()}
          >
            Stop
          </Button>
          <Button
            disabled={client !== undefined}
            color="success"
            onClick={async () => {
              const agentMap = new Map<string, BaseAgent>();
              for (const agent of agents) {
                agentMap.set(agent.visual._id, agent);
              }
              const client = new ScenarioClient(wr, { ...scenario! });
              setClient(client);
              await client.start();
            }}
          >
            Run
          </Button>
          <Button
            disabled={client !== undefined}
            color="success"
            onClick={async () => {
              await commitChanges();
              const agentMap = new Map<string, BaseAgent>();
              for (const agent of agents) {
                agentMap.set(agent.visual._id, agent);
              }
              const client = new ScenarioClient(wr, { ...scenario! });
              setClient(client);
              await client.build();
            }}
          >
            Build
          </Button>
          <Button
            color="secondary"
            onClick={async () => {
              if (!editedScenario) return;
              await commitChanges();
              setIsEditing(false);
              wr.emitEvent(WREvents.ScenarioSelected, null);
            }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setIsEditing(false);
              wr.emitEvent(WREvents.ScenarioSelected, null);
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Agent Accordions */}
      <Box
        sx={{ overflow: "auto", width: size.width, height: size.height - 48 }}
      >
        {agents.map((agent) => (
          <Accordion
            key={agent.visual._id}
            disableGutters
            expanded={isOpen(agent.visual._id)}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              onClick={() => toggleExpand(agent.visual._id)}
            >
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {agent.getFriendlyId()}
              </Typography>
              {/* <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); togglePin(agent.visual._id); }}
              >
                {pinned.has(agent.visual._id) ? <PushPin /> : <PushPinOutlined />}
              </IconButton> */}
            </AccordionSummary>
            <AccordionDetails>
              <RenderAccordionToolbar
                pinned={pinned}
                panel={agent.visual._id}
                togglePin={togglePin}
              />
              {!haveBlocksForAgent(agent) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    const newBlock = {
                      id: "Startup",
                      actorId: agent.visual._id,
                      type: "reset",
                      text: "New Block",
                      isBuilt: false,
                      outputs: [],
                      position: { x: 0, y: 0 },
                    };
                    mixer({ blocks: [...editedScenario.blocks, newBlock] });
                  }}
                >
                  Add Block
                </Button>
              ) : (
                <Box sx={{ padding: 0.25, height: "300px" }}>
                  <AgentBlocks
                    agent={agent}
                    scenario={editedScenario}
                    setScenario={setEditedScenario}
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </div>
  );
}
